import { analyticsWarehouseService } from "./services/analyticsWarehouseService";
import { pickDefaultAnalyticsWarehouse } from "./analyticsWarehouses";

/**
 * Format a Date as a local YYYY-MM-DD calendar day (no UTC day-shift).
 * Shared by Inventory Lost Sales params and the Lost Sales Analysis dashboard.
 */
export const toLostSalesDateString = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

/**
 * Default analysis window: end = yesterday (local; today is a partial day),
 * start = same calendar date one year before that
 * (e.g. on 2026-09-01: 2025-08-31 → 2026-08-31).
 */
export const getDefaultLostSalesDateRange = (now = new Date()) => {
  const end = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
  const start = new Date(end);
  start.setFullYear(start.getFullYear() - 1);
  return {
    start_date: toLostSalesDateString(start),
    end_date: toLostSalesDateString(end),
  };
};

/**
 * Single-scope request. Calculation parameters (search window, day-of-week
 * adjustment, safe period) are deliberately omitted: the server applies the
 * client's Lost Sales settings (Client Config → Dashboard settings).
 */
export const buildLostSalesPagePayload = ({
  productId,
  warehouseId,
  clientId,
  sku,
  startDate,
  endDate,
}) => ({
  product: productId,
  warehouse_id: warehouseId,
  client: clientId,
  sku,
  start_date: startDate,
  end_date: endDate,
});

export const firstListRecord = (response) => {
  if (Array.isArray(response?.data) && response.data.length) {
    return response.data[0];
  }
  if (Array.isArray(response) && response.length) {
    return response[0];
  }
  return null;
};

export const childSkusFromProduct = (product) => [
  ...new Set(
    (product?.variants || [])
      .map((variant) => variant?.sku)
      .filter(Boolean),
  ),
];

/**
 * Resolve the default analysis warehouse for the current client (the client's
 * default analytics warehouse when offered, else the first one).
 *
 * No product or SKU is pre-selected: the first catalog row is arbitrary and
 * often has no inventory or sales in the period, which made the page open on
 * a selection that could only fail. The scope picker leads with SKUs that do
 * have data instead.
 */
export const resolveDefaultLostSalesWarehouse = async (
  clientId,
  {
    // Analytics-visible warehouses only (the client's default when offered).
    fetchWarehouses = (id) => analyticsWarehouseService.fetch({ clientId: id, limit: 1 }),
  } = {},
) => {
  if (!clientId) return null;
  const warehouseList = await fetchWarehouses(clientId);
  const warehouse =
    pickDefaultAnalyticsWarehouse(warehouseList) || firstListRecord(warehouseList);
  return warehouse?._id ? warehouse : null;
};
